import { useQuery } from '@tanstack/react-query';
import { 
  db, 
  collection, 
  doc, 
  getDoc 
} from '../firebase';

// Default data structure for new installations
const defaultData = {
  business: {
    businessName: "",
    contact: {
      contactNumber: "",
      faxNumber: "",
      email: '',
      businessAddress: "",
      googleMapsLocation: "",
      businessHours: []
    },
    socialMedia: {
      facebook: { enable: false, url: "" },
      instagram: { enable: false, url: "" },
      yelp: { enable: false, url: "" },
      tripAdvisor: { enable: false, url: "" }
    }
  },
  about: {
    storyData: {
      title: "",
      imageUrl: "",
      paragraphs: []
    }
  },
  catering: {
    miniumDaysAdvance: 2,
    minimumPeople: 10
  },
  gallery: [],
  hero: {
    backgroundType: "image",
    backgroundImage: "",
    backgroundColor: "",
    tagline: "",
    title: "",
    description: "",
    buttonLabel: ""
  },
  reviews: [],
  stats: [],
  services: [
    {
      title: 'Dine-In Experience',
      description: 'Enjoy our warm ambiance and exceptional service in our comfortable dining space.',
      icon: 'dineIn'
    },
    {
      title: 'Takeaway',
      description: 'Quick and convenient pickup service for enjoying our dishes at home.',
      icon: 'takeaway'
    },
    {
      title: 'Catering Services',
      description: 'Make your events special with our professional catering services.',
      icon: 'catering'
    }
  ],
};

const useSiteData = () => {
  return useQuery({
    queryKey: ['siteData'],
    queryFn: async () => {
      try {
        // Fetch all data in parallel
        const [businessDoc, aboutDoc, heroDoc, cateringDoc, statsDoc, reviewsDoc, galleryDoc, servicesDoc] = 
          await Promise.all([
            getDoc(doc(collection(db, 'business'), 'singleton')),
            getDoc(doc(collection(db, 'about'), 'singleton')),
            getDoc(doc(collection(db, 'hero'), 'singleton')),
            getDoc(doc(collection(db, 'catering'), 'singleton')),
            getDoc(doc(collection(db, 'stats'), 'singleton')),
            getDoc(doc(collection(db, 'reviews'), 'singleton')),
            getDoc(doc(collection(db, 'gallery'), 'singleton')),
            getDoc(doc(collection(db, 'services'), 'singleton')),
          ]);

        // Process data with fallbacks to empty defaults
        const fetchedData = {
          business: businessDoc.exists() ? businessDoc.data() : defaultData.business,
          about: aboutDoc.exists() ? aboutDoc.data() : defaultData.about,
          hero: heroDoc.exists() ? heroDoc.data() : defaultData.hero,
          catering: cateringDoc.exists() ? cateringDoc.data() : defaultData.catering,
          stats: statsDoc.exists() ? statsDoc.data().items || [] : defaultData.stats,
          reviews: reviewsDoc.exists() ? reviewsDoc.data().reviews || [] : defaultData.reviews,
          gallery: galleryDoc.exists() ? galleryDoc.data().items || [] : defaultData.gallery,
          services: servicesDoc.exists() ? servicesDoc.data().items || [] : defaultData.services,
        };

        // Log fetched data for debugging
        console.log('Fetched site data:', fetchedData);
        return fetchedData;
      } catch (err) {
        console.error("Error fetching data:", err);
        throw err;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 30, // 30 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });
};

export default useSiteData;